/**
 * AdminMenu — persistent left rail for the Administration surface.
 * Design contract §5.5.
 *
 * - 240 px wide (matches --shell-sidebar-w, same slot as CategorySidebar)
 * - Mutually exclusive with CategorySidebar: only one appears per route
 * - Six items: Tenants | Sub-clients | Auth | Billing | Audit log | Runtime config
 * - Active item: white background, --shadow-1
 * - Role-gated: core-developer and vertical-product-owner per brief §3 stage 4
 *
 * Refs: marketplace-broker-c2 brief §3 stage 4, design-contract.md §5.5
 */

import type { Route } from '../shell/Router';

export type AdminRoute = '/admin' | '/admin/sub-clients';

interface AdminMenuProps {
  activePath: Route;
  onNavigate: (path: AdminRoute) => void;
}

interface MenuItem {
  label: string;
  path: AdminRoute;
  isPlaceholder: boolean;
}

const MENU_ITEMS: MenuItem[] = [
  { label: 'Tenants', path: '/admin', isPlaceholder: true },
  { label: 'Sub-clients', path: '/admin/sub-clients', isPlaceholder: false },
  { label: 'Auth', path: '/admin', isPlaceholder: true },
  { label: 'Billing', path: '/admin', isPlaceholder: true },
  { label: 'Audit log', path: '/admin', isPlaceholder: true },
  { label: 'Runtime config', path: '/admin', isPlaceholder: true },
];

export default function AdminMenu({ activePath, onNavigate }: AdminMenuProps) {
  return (
    <aside
      className="dp-shell__sidebar"
      style={{
        background: 'var(--bg-recessed)',
        borderRightWidth: '1px',
        borderRightStyle: 'solid',
        borderRightColor: 'var(--border-subtle)',
        padding: 'var(--sp-6) 0',
        flexShrink: 0,
      }}
      aria-label="Administration menu"
    >
      <p
        className="dp-eyebrow"
        style={{
          padding: '0 var(--sp-5)',
          marginBottom: 'var(--sp-3)',
          margin: '0 0 var(--sp-3)',
        }}
      >
        Administration
      </p>

      <ul role="list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {MENU_ITEMS.map((item, idx) => {
          // Active detection: Sub-clients item active when on /admin/sub-clients;
          // Tenants item active when on /admin itself.
          const isActive =
            item.label === 'Sub-clients'
              ? activePath === '/admin/sub-clients'
              : item.label === 'Tenants'
              ? activePath === '/admin'
              : false;

          return (
            <li key={`${item.label}-${idx}`}>
              <button
                onClick={() => onNavigate(item.path)}
                aria-current={isActive ? 'page' : undefined}
                style={{
                  width: '100%',
                  padding: 'var(--sp-2) var(--sp-5)',
                  background: isActive ? 'var(--bg-surface)' : 'transparent',
                  boxShadow: isActive ? 'var(--shadow-1)' : 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body-sm)',
                  fontWeight: 500,
                  color: isActive ? 'var(--fg-1)' : 'var(--fg-2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'background var(--dur-fast) var(--ease-out)',
                }}
                onMouseEnter={(e) => {
                  if (!isActive)
                    (e.currentTarget as HTMLButtonElement).style.background =
                      'rgba(255,255,255,0.5)';
                }}
                onMouseLeave={(e) => {
                  if (!isActive)
                    (e.currentTarget as HTMLButtonElement).style.background =
                      'transparent';
                }}
              >
                {item.label}
                {item.isPlaceholder && (
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 'var(--fs-meta)',
                      color: 'var(--fg-4)',
                      background: 'var(--dp-canvas-300)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '1px 5px',
                    }}
                  >
                    soon
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
